import { Modal, SubmitButton } from "@xola/ui-kit";
import React from "react";
import { DeleteCatalogItemConflictReason, UnitType } from "types";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";

export interface DeleteConflictError {
    reason?: DeleteCatalogItemConflictReason;
    conflictingData?: string[];
    message?: string;
}

interface DeleteCatalogItemModalProps {
    isOpen: boolean;
    unitType: UnitType;
    conflictError?: DeleteConflictError;
    onClose: () => void;
    onDelete: () => void;
}

export const DeleteCatalogItemModal = ({
    isOpen,
    unitType,
    conflictError,
    onClose,
    onDelete,
}: DeleteCatalogItemModalProps) => {
    const handleConfirmDelete = () => {
        onClose();
        onDelete();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            {conflictError ? (
                <ModalContent
                    unitType={unitType}
                    conflictError={conflictError}
                    onClose={onClose}
                    onDelete={handleConfirmDelete}
                />
            ) : null}
        </Modal>
    );
};

type ModalContentProps = Omit<DeleteCatalogItemModalProps, "isOpen">;

const ModalContent = ({ conflictError, unitType, onClose, onDelete }: ModalContentProps) => {
    const hasLinkedProducts = conflictError?.reason === DeleteCatalogItemConflictReason.CATALOG_ITEM_LINKED_TO_PRODUCTS;
    const hasSingleDemographicProducts =
        conflictError?.reason === DeleteCatalogItemConflictReason.PRODUCT_HAS_NO_DEMOGRAPHIC_CATALOG_ITEM;
    const experienceNames = conflictError?.conflictingData ?? [];

    return (
        <>
            <Modal.Header>{hasLinkedProducts ? "Confirm Deletion" : "Unable to Delete"}</Modal.Header>
            <Modal.Body className="text-gray-darker">
                {hasLinkedProducts ? (
                    <LinkedProductsContent experienceNames={experienceNames} unitType={unitType} />
                ) : null}
                {hasSingleDemographicProducts ? (
                    <SingleDemographicProductContent experienceNames={experienceNames} unitType={unitType} />
                ) : null}
                {hasLinkedProducts || hasSingleDemographicProducts
                    ? null
                    : conflictError?.message ?? `Error in deleting ${unitType}.`}
            </Modal.Body>
            <Modal.Footer className="space-x-2">
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                {hasLinkedProducts ? (
                    <SubmitButton color="danger" onClick={onDelete}>
                        Confirm
                    </SubmitButton>
                ) : null}
            </Modal.Footer>
        </>
    );
};

const LinkedProductsContent = ({ experienceNames, unitType }: { experienceNames: string[]; unitType: UnitType }) => {
    return (
        <>
            Deleting this {unitType} will remove it from all the following products:
            {experienceNames.length > 0 ? (
                <ul className="no-translate my-6 list-inside list-disc">
                    {experienceNames.map((experienceName, index) => {
                        return (
                            // eslint-disable-next-line react/no-array-index-key
                            <li key={index}>
                                <NoTranslate>{experienceName}</NoTranslate>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
            Are you sure you want to delete?
        </>
    );
};

const SingleDemographicProductContent = ({
    experienceNames,
    unitType,
}: {
    experienceNames: string[];
    unitType: UnitType;
}) => {
    return (
        <>
            Deleting this {unitType} will leave the following products without any demographics:
            {experienceNames.length > 0 ? (
                <ul className="no-translate my-6 list-inside list-disc">
                    {experienceNames.map((experienceName, index) => {
                        return (
                            // eslint-disable-next-line react/no-array-index-key
                            <li key={index}>
                                <NoTranslate>{experienceName}</NoTranslate>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
            If you&apos;d still like to delete this demographic, please associate additional demographics with the above
            products and try again.
        </>
    );
};
